import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { config } from '@app/core/app-config';
import { FilterComponent } from '@app/shared/components/filter/filter.component';
import { PaginationMeta } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { FilterService } from '@app/shared/services/filter/filter.service';
import { LogService } from '@app/shared/services/logs/log.service';
import { TitleService } from '@app/shared/services/title/title.service';

@Component({
  standalone: false,
  selector: 'sbnb-log',
  templateUrl: './log.component.html',
  styleUrls: ['./log.component.scss'],
})
export class LogComponent implements OnInit {
  type: any;
  logs: any[];
  loading: boolean;
  paginationData: PaginationMeta;
  searchCriteria: string;
  currentOffset: number;
  activeFilters: Filter[];
  segmentFilterDefaults: any;
  defaultFilterOfPending: boolean;

  typeInfo = [
    {
      type: 'events',
      apiType: 'activities',
      label: 'Event messages log',
      editUrl: '/gx/log/activities/',
      filterKey: 'gx-logs/activity',
    },
    {
      type: 'scheduled',
      apiType: 'scheduled',
      label: 'Scheduled messages log',
      editUrl: '/gx/log/scheduled/',
      filterKey: 'gx-logs/scheduled',
    },
    {
      type: 'questions',
      apiType: 'questions',
      label: 'Questions log',
      editUrl: '/gx/log/questions/',
      filterKey: 'gx-logs/questions',
    },
    {
      type: 'reviews',
      apiType: 'reviews',
      label: 'Reviews log',
      editUrl: '/gx/log/reviews/',
      filterKey: 'gx-logs/reviews',
    },
  ];

  @ViewChild('filterComponent')
  filterComponent: FilterComponent;

  constructor(
    private logService: LogService,
    private titleService: TitleService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private filter: FilterService
  ) {}

  ngOnInit() {
    this.loading = true;

    const type = this.route.snapshot.paramMap.get('type');
    this.type = this.typeInfo.find((x) => x.type === type);

    if (!this.type) {
      this.router.navigate(['/gx/log/events']);
    }

    const pending = this.route.snapshot.queryParamMap.get('pending');

    if (pending) {
      this.defaultFilterOfPending = true;
    }

    this.titleService.manuallySet(`Guest Experience ${this.type.label} logs`);

    this.currentOffset = +this.route.snapshot.paramMap.get('offset') || 0;
    this.searchCriteria = this.route.snapshot.paramMap.get('query');

    this.activeFilters = [];
    this.segmentFilterDefaults = [];

    // If we stashed filters for this key, retrieve and set them
    const stashedFilters = this.filter.retrieveStashedValuesForKey('logs');
    if (stashedFilters) {
      this.activeFilters = stashedFilters;
      this.segmentFilterDefaults = this.activeFilters;
    } else {
      this.addDefaultFilters();
    }

    this.loadLogs(this.activeFilters, this.currentOffset, this.searchCriteria);
  }

  generateQuestionTitle(log) {
    let title = '';

    if (log.attributes?.length > 0) {
      const intents = log.attributes.find((x) => x.type === 'intent_link_list');

      if (intents.data) {
        Object.keys(intents.data).forEach((element) => {
          title += `${element},`;
        });
      }
    }

    // Strip the last comma
    if (title.length > 0) {
      title = title.substring(0, title.length - 1);
    }

    return title;
  }

  addDefaultFilters() {
    if (this.type.type === 'reviews' || this.type.type === 'scheduled') {
      this.activeFilters = [
        {
          id: `gx-logs-${this.type.type}-status`,
          uuid: 'notused',
          label: 'Status',
          lockable: false,
          type: 'list',
          values: `${config.API_URL}/filter-values/gx-logs/message-statuses`,
          selectedValue: {
            key: 'sent',
            label: 'Sent',
          },
          valuesData: [
            { key: 'cancelled', label: 'Cancelled' },
            { key: 'failed', label: 'Failed' },
            { key: 'pending', label: 'Pending' },
            { key: 'sent', label: 'Sent' },
          ],
        },
      ];

      if (this.defaultFilterOfPending) {
        this.activeFilters = [
          {
            id: `gx-logs-${this.type.type}-status`,
            uuid: 'notused',
            label: 'Status',
            lockable: false,
            type: 'list',
            values: `${config.API_URL}/filter-values/gx-logs/message-statuses`,
            selectedValue: {
              key: 'pending',
              label: 'Pending',
            },
            valuesData: [
              { key: 'cancelled', label: 'Cancelled' },
              { key: 'failed', label: 'Failed' },
              { key: 'pending', label: 'Pending' },
              { key: 'sent', label: 'Sent' },
            ],
          },
        ];
      }
    }

    this.segmentFilterDefaults = this.activeFilters;
  }

  loadLogs(filters: Filter[], offset?: number, search?: string) {
    this.loading = true;

    this.logService.getLogs(this.type.apiType, filters, offset, search).subscribe((res) => {
      this.logs = res.data;
      this.paginationData = res.meta?.pagination;
      this.loading = false;

      this.logs.forEach((log) => {
        if (log.sent_at) {
          log.send_status = 'sent';
        }

        if (log.cancelled_at) {
          log.send_status = 'cancelled';
        }

        if (log.scheduled_for && !log.cancelled_at && !log.sent_at) {
          log.send_status = 'scheduled';
        }

        if (!log.scheduled_for && !log.cancelled_at && !log.sent_at) {
          log.send_status = 'prepared';
        }

        if (log.failed) {
          log.send_status = 'failed';
        }

        if (log.closed) {
          log.send_status = 'detected';
        }
      });
    });
  }

  public searchUpdated(searchVal: string) {
    // This is already debounced by the child presentation component
    const url = this.router.createUrlTree([{ query: searchVal }], { relativeTo: this.route }).toString();

    this.location.go(url);

    this.searchCriteria = searchVal;

    this.loadLogs(this.activeFilters, 0, this.searchCriteria);
  }

  public handleFilterChanged(filters: Filter[]) {
    this.activeFilters = filters;

    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('logs', this.activeFilters);

    // Retrieve conversations based on the new filters
    this.loadLogs(this.activeFilters, 0, this.searchCriteria);

    // Zero out the offset from the URL - we have fresh data so need to go back to offset 0
    const url = this.router
      .createUrlTree([this.searchCriteria ? { query: this.searchCriteria } : {}], {
        relativeTo: this.route,
      })
      .toString();

    this.location.go(url);
  }

  filterReset() {
    this.activeFilters = [];
    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('logs', this.activeFilters);

    this.filterComponent.selfReset();
    this.ngOnInit();
  }

  public handlePageChanged(newPageNo: number) {
    this.currentOffset = newPageNo;

    // We use offsets as the API deals in offsets rather than pages, when requesting data
    const newOffset = newPageNo * this.paginationData.per_page - this.paginationData.per_page;

    // Commit the new offset to the URL - our source of truth for offsets
    const url = this.router
      .createUrlTree(
        [
          {
            offset: newOffset,
            query: this.searchCriteria ? this.searchCriteria : '',
          },
        ],
        { relativeTo: this.route }
      )
      .toString();

    this.location.go(url);
    this.titleService.manuallySet('Custom codes');

    // Reload Conversation Threads based on new offset data, with existing filters
    this.loadLogs(this.activeFilters, newOffset, this.searchCriteria);
  }
}
